import { useEffect, useState } from 'react';
import {
  fetchKPIs,
  fetchKPIHistory,
  fetchMLForecasts,
  fetchStoreAverageKPIs,
} from '../api/dashboardApi';

/**
 * Data for the signed-in person's own POS record (home_store + stylist_name_pos),
 * independent of what Home is currently showing.
 *
 * The AI chat is the user's own: its dossier is always about them (owner decision
 * 2026-10-04), even while a manager browses other stylists on Home. When Home already
 * shows the user's own record the context data is reused and nothing is fetched.
 *
 * Returns null fields until loaded; callers fall back to the context data.
 */
export default function useOwnDashboardData({ store, stylist, month, enabled }) {
  const [state, setState] = useState({ key: null, kpis: null, kpiHistory: null, mlForecasts: null, storeAverageKpis: null });

  const key = enabled && store && stylist ? `${store}|${stylist}|${month || ''}` : null;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    (async () => {
      const [kpisRes, histRes, fcRes, avgRes] = await Promise.allSettled([
        fetchKPIs({ store, stylist, month }),
        fetchKPIHistory({ store, stylist }),
        fetchMLForecasts({ store, stylist }),
        fetchStoreAverageKPIs({ store, month }),
      ]);
      if (cancelled) return;
      const val = (r, pick) => (r.status === 'fulfilled' ? pick(r.value) : null);
      setState({
        key,
        kpis: val(kpisRes, (d) => d?.kpis ?? null),
        kpiHistory: val(histRes, (d) => d?.history ?? []),
        mlForecasts: val(fcRes, (d) => d?.forecasts ?? []),
        storeAverageKpis: val(avgRes, (d) => d?.kpis ?? null),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [key, store, stylist, month]);

  if (!key || state.key !== key) {
    return { ready: false, kpis: null, kpiHistory: null, mlForecasts: null, storeAverageKpis: null };
  }
  return { ready: true, ...state };
}
